import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setUserContext, clearUserContext } from "@/lib/sentry";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to parse OAuth tokens from URL hash (for full-page redirect flow)
function parseOAuthTokensFromHash(): { access_token: string; refresh_token: string } | null {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token')) {
    return null;
  }
  
  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  
  if (accessToken && refreshToken) {
    // Clean up the URL
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return { access_token: accessToken, refresh_token: refreshToken };
  }
  
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener BEFORE checking session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        
        // Update Sentry user context for error tracking
        if (session?.user) {
          setUserContext(session.user.id, session.user.email);
        } else {
          clearUserContext();
        }
      }
    );

    // Check for OAuth tokens in URL hash (from full-page redirect flow)
    const oauthTokens = parseOAuthTokensFromHash();
    if (oauthTokens) {
      // Set session from OAuth tokens
      supabase.auth.setSession(oauthTokens).then(({ data: { session }, error }) => {
        if (error) {
          console.error("Error setting OAuth session:", error);
        }
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      });
    } else {
      // Check for existing session
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        if (error) {
          // Handle invalid refresh token - clear the session
          console.warn("Session error, clearing auth state:", error.message);
          supabase.auth.signOut();
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
        setIsLoading(false);
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Don't use Supabase's default email - we send our own branded email
        emailRedirectTo: undefined,
      },
    });
    
    // If signup successful, send our custom verification email
    if (!error && data.user) {
      try {
        await supabase.functions.invoke('send-verification-email', {
          body: { email }
        });
      } catch (emailError) {
        console.error('Failed to send verification email:', emailError);
        // Don't fail the signup if email fails - user can resend
      }
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
